import { memo, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Path from 'path-to-regexp';

import { ROUTE_NEW_LINK } from 'constant/routes';
import { motion } from 'motion/react';

import { Link, PrioritySettings, GroupActionsList } from './groupActionsList';
import { NextAction, Tasks } from './actionList.types';
import { Skeleton } from '@/shadcn/skeleton';
import { Button } from '@/shadcn/button';
import { Empty } from '../empty/empty';

interface ActionListProps {
  workflowId: string;
  nextActions?: NextAction[];
  tasks?: Tasks | null;
  traceIds?: string[];
  toggleTray?: () => void;
  loading?: boolean;
  canSelect?: boolean;
}

export const ActionsList = memo(
  ({
    workflowId,
    nextActions = [],
    tasks = null,
    traceIds = [],
    toggleTray = () => {},
    loading = false,
    canSelect = false
  }: ActionListProps) => {
    const history = useHistory();
    const [selectedLink, setSelectedLink] = useState<Partial<Link>>({});

    const actionsHandler = useCallback(() => {
      const { groupKey, actionKey } = selectedLink;

      const baseUrl = Path.compile(ROUTE_NEW_LINK)({
        wfid: workflowId
      });
      return history.push(
        `${baseUrl}?groupKey=${groupKey}&actionKey=${actionKey}${
          traceIds !== null ? `&traceIds=${traceIds.join(',')}` : ''
        }`,
        {
          from: history.location,
          goToTraceInspector: traceIds.length === 0
        }
      );
    }, [selectedLink, workflowId, history, traceIds]);

    const usedNextActions = nextActions || [];

    const getPrioritySettings = (
      nextAction: NextAction
    ): PrioritySettings | undefined => {
      if (!tasks || !tasks.groups) return undefined;
      const todos = tasks.groups?.[nextAction.group?.label]?.todo;
      if (!todos?.length) return undefined;

      return {
        priorityActionKey: todos,
        priorityGroupLabel: nextAction.group.label // if we are here, then this label is in the tasks groups
      };
    };

    const SingleActionSkeleton = () => {
      return (
        <div className="flex flex-row items-center gap-2">
          <Skeleton className="aspect-square w-5 rounded-full" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      );
    };

    const ActionListSkeleton = () => {
      return (
        <div className="flex flex-col gap-2 pr-4">
          <div className="flex h-[40px] flex-row items-center gap-3 pt-4 pl-2">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <Skeleton className="h-5 w-full" />
          </div>
          <div className="flex flex-col gap-2 pt-2 pl-[53px]">
            <SingleActionSkeleton />
            <SingleActionSkeleton />
          </div>
        </div>
      );
    };

    const isEmpty = !loading && traceIds.length > 0 && nextActions.length === 0;
    const nextActionsEmpty = !loading && nextActions.length == 0;
    const nothingSelected =
      canSelect &&
      !loading &&
      traceIds.length === 0 &&
      nextActions.length === 0;

    const EmptyComponent = ({ message }: { message: string }) => {
      return (
        <div className="flex h-2/3 flex-col items-center justify-center">
          <Empty className="size-[100px]" message={message} />
        </div>
      );
    };

    function calculateDelay(nextAction: NextAction[]) {
      const total = nextAction.reduce((acc, action) => {
        return acc + action.actions.length;
      }, 0);

      if (total < 10) return 0.1;

      if (total < 20) return 0.05;

      return 0.03;
    }

    const delay = calculateDelay(usedNextActions);

    return (
      <div className="flex size-full flex-col justify-between overflow-auto transition-all">
        <div className="scrollbar-thin w-full grow overflow-auto">
          {/* we keep the previous next actions while loading the new ones */}
          {loading && !nextActionsEmpty && <ActionListSkeleton />}
          {!loading &&
            usedNextActions.map((nextAction, index) => (
              <motion.div
                key={nextAction.group.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.2,
                  delay: 0.05 + delay * index
                }}
              >
                <GroupActionsList
                  key={nextAction.group.label}
                  groupActions={nextAction}
                  priority={getPrioritySettings(nextAction)}
                  selectedLink={selectedLink}
                  setSelectedLink={setSelectedLink}
                />
              </motion.div>
            ))}
          {isEmpty && <EmptyComponent message="No actions available" />}
          {nothingSelected && (
            <EmptyComponent message="No trace selected, please select a trace to perform an action" />
          )}
        </div>
        <footer className="mt-2 flex shrink-0 justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => toggleTray()}
            data-cy="cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={Object.keys(selectedLink).length === 0}
            onClick={() => actionsHandler()}
            data-cy="perform-next-action-button"
          >
            Perform action
          </Button>
        </footer>
      </div>
    );
  }
);

export default ActionsList;
